import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, Check, QrCode, FileText, CreditCard, Zap } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  useCreateSubscription,
  usePlans,
  formatBrl,
  type PlanKey,
  type Cycle,
  type PaymentMethod,
  type CreateSubscriptionInput,
} from '@/lib/hooks/billing';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPlan: string | null;
}

interface FormValues {
  name: string;
  email: string;
  cpfCnpj: string;
  phone: string;
}

function fmtCpfCnpj(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 14);
  if (d.length <= 11) {
    // CPF
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
    if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }
  // CNPJ
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function fmtPhone(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d;
  if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export function SubscribeDrawer({ open, onOpenChange, currentPlan }: Props) {
  const plansQuery = usePlans();
  const createMut = useCreateSubscription();

  const [plan, setPlan] = useState<PlanKey>('PRO');
  const [cycle, setCycle] = useState<Cycle>('MONTHLY');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('PIX');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    defaultValues: { name: '', email: '', cpfCnpj: '', phone: '' },
  });

  const cpfCnpjValue = watch('cpfCnpj');
  const phoneValue = watch('phone');

  useEffect(() => {
    if (!open) {
      reset({ name: '', email: '', cpfCnpj: '', phone: '' });
      setPlan((currentPlan as PlanKey) ?? 'PRO');
      setCycle('MONTHLY');
      setPaymentMethod('PIX');
    }
  }, [open, reset, currentPlan]);

  async function onSubmit(values: FormValues) {
    if (plan === 'ENTERPRISE') {
      toast.error('Plano empresarial requer contato comercial.');
      return;
    }
    const payload: CreateSubscriptionInput = {
      plan: plan as 'BASIC' | 'PRO',
      cycle,
      paymentMethod,
      customer: {
        name: values.name,
        email: values.email,
        cpfCnpj: values.cpfCnpj,
        ...(values.phone ? { phone: values.phone } : {}),
      },
    };
    try {
      await createMut.mutateAsync(payload);
      toast.success('Assinatura criada! Veja a cobrança gerada abaixo.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar assinatura.');
    }
  }

  const plans = plansQuery.data?.plans ?? [];
  const billable = plans.filter((p) => p.key !== 'ENTERPRISE');
  const selected = billable.find((p) => p.key === plan);
  const displayValue = selected ? (cycle === 'YEARLY' ? selected.yearlyValue : selected.monthlyValue) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Assinatura"
            title={currentPlan ? 'Trocar plano' : 'Assinar GestAKI'}
            description="Escolha plano, ciclo e forma de pagamento."
          />
          <SheetBody className="space-y-6">
            {/* PLANO */}
            <section>
              <Label className="mb-2 block">Plano</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {billable.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPlan(p.key)}
                    className={cn(
                      'text-left p-4 rounded-2 border transition-all',
                      plan === p.key
                        ? 'border-mint-deep bg-mint-soft shadow-pressed'
                        : 'border-card-stroke bg-card hover:border-mint-deep/30',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2 mb-2">
                      <span className="font-display font-semibold text-fg text-[16px] tracking-[-0.015em]">
                        {p.name}
                      </span>
                      {plan === p.key && (
                        <Check className="h-4 w-4 text-mint-deep" strokeWidth={2} />
                      )}
                    </div>
                    <p className="text-xs text-fg-muted mb-3 leading-snug">{p.description}</p>
                    <p className="font-display text-[20px] font-bold text-fg tracking-[-0.02em]">
                      {formatBrl(cycle === 'YEARLY' ? p.yearlyValue : p.monthlyValue)}
                      <span className="text-xs font-mono text-fg-muted ml-1">/mês</span>
                    </p>
                    <ul className="mt-3 space-y-1">
                      {p.features.slice(0, 3).map((f) => (
                        <li key={f} className="text-[11px] text-fg-muted flex items-center gap-1">
                          <Check className="h-3 w-3 text-mint-deep shrink-0" strokeWidth={2} />
                          {f}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
            </section>

            {/* CICLO */}
            <section>
              <Label className="mb-2 block">Ciclo</Label>
              <div className="grid grid-cols-2 gap-2">
                <CycleButton
                  active={cycle === 'MONTHLY'}
                  onClick={() => setCycle('MONTHLY')}
                  title="Mensal"
                  subtitle="Cobrança todo mês"
                />
                <CycleButton
                  active={cycle === 'YEARLY'}
                  onClick={() => setCycle('YEARLY')}
                  title="Anual"
                  subtitle="Cobrança mensal · ~15% off"
                  badge="economize"
                />
              </div>
            </section>

            {/* MÉTODO */}
            <section>
              <Label className="mb-2 block">Forma de pagamento</Label>
              <div className="grid grid-cols-3 gap-2">
                <PaymentButton
                  active={paymentMethod === 'PIX'}
                  onClick={() => setPaymentMethod('PIX')}
                  icon={QrCode}
                  label="PIX"
                  hint="Aprovação instantânea"
                />
                <PaymentButton
                  active={paymentMethod === 'CREDIT_CARD'}
                  onClick={() => setPaymentMethod('CREDIT_CARD')}
                  icon={CreditCard}
                  label="Cartão"
                  hint="Recorrente automático"
                />
                <PaymentButton
                  active={paymentMethod === 'BOLETO'}
                  onClick={() => setPaymentMethod('BOLETO')}
                  icon={FileText}
                  label="Boleto"
                  hint="Vence em 3 dias"
                />
              </div>
              {paymentMethod === 'CREDIT_CARD' && (
                <p className="mt-2 text-[11px] text-fg-muted leading-snug">
                  Após confirmação, o Asaas envia um email com o link pra cadastrar o cartão de forma
                  segura (dados não passam pelo GestAKI).
                </p>
              )}
            </section>

            <hr className="border-card-stroke" />

            {/* DADOS DO PAGADOR */}
            <section>
              <h4 className="font-display text-[14px] font-semibold text-fg mb-3 tracking-[-0.01em]">
                Dados de cobrança
              </h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="name" className="mb-1.5 block">Nome / Razão social</Label>
                  <Input
                    id="name"
                    placeholder="Nome completo do pagador"
                    {...register('name', {
                      required: 'Obrigatório',
                      minLength: { value: 2, message: 'Mínimo 2 caracteres' },
                    })}
                  />
                  {errors.name && (
                    <p className="text-[11px] text-warn mt-1">{errors.name.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="email" className="mb-1.5 block">Email pra recibo</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="voce@suaentidade.org.br"
                    {...register('email', {
                      required: 'Obrigatório',
                      pattern: { value: /\S+@\S+\.\S+/, message: 'Email inválido' },
                    })}
                  />
                  {errors.email && (
                    <p className="text-[11px] text-warn mt-1">{errors.email.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="cpfCnpj" className="mb-1.5 block">CPF ou CNPJ</Label>
                  <Input
                    id="cpfCnpj"
                    inputMode="numeric"
                    placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    value={cpfCnpjValue}
                    {...register('cpfCnpj', {
                      required: 'Obrigatório',
                      validate: (v) => {
                        const d = v.replace(/\D/g, '');
                        return d.length === 11 || d.length === 14 || 'CPF (11) ou CNPJ (14)';
                      },
                    })}
                    onChange={(e) => setValue('cpfCnpj', fmtCpfCnpj(e.target.value))}
                  />
                  {errors.cpfCnpj && (
                    <p className="text-[11px] text-warn mt-1">{errors.cpfCnpj.message}</p>
                  )}
                </div>
                <div>
                  <Label htmlFor="phone" className="mb-1.5 block">
                    Telefone <span className="text-fg-dim font-normal">(opcional)</span>
                  </Label>
                  <Input
                    id="phone"
                    inputMode="numeric"
                    placeholder="(11) 99999-9999"
                    value={phoneValue}
                    {...register('phone')}
                    onChange={(e) => setValue('phone', fmtPhone(e.target.value))}
                  />
                </div>
              </div>
            </section>

            {/* RESUMO */}
            <div className="rounded-2 border border-card-stroke bg-card-soft p-4">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-fg-muted">Total mensal</span>
                <span className="font-display text-[24px] font-bold text-fg tracking-[-0.02em]">
                  {formatBrl(displayValue)}
                </span>
              </div>
              <p className="text-[11px] text-fg-muted mt-1">
                Primeira cobrança em 3 dias. Cancelamento a qualquer momento.
              </p>
            </div>
          </SheetBody>
          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">Cancelar</Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Processando…
                </>
              ) : (
                <>
                  <Zap className="h-4 w-4" strokeWidth={1.8} />
                  Confirmar assinatura
                </>
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function CycleButton({
  active,
  onClick,
  title,
  subtitle,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left p-3 rounded-2 border transition-all relative',
        active
          ? 'border-mint-deep bg-mint-soft shadow-pressed'
          : 'border-card-stroke bg-card hover:border-mint-deep/30',
      )}
    >
      {badge && (
        <span className="absolute top-1.5 right-1.5 font-mono text-[9px] uppercase tracking-wider text-mint-deep bg-card px-1.5 py-0.5 rounded">
          {badge}
        </span>
      )}
      <p className="font-display font-semibold text-fg text-[14px]">{title}</p>
      <p className="text-[11px] text-fg-muted mt-0.5">{subtitle}</p>
    </button>
  );
}

function PaymentButton({
  active,
  onClick,
  icon: Icon,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof QrCode;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex flex-col items-center justify-center gap-1 p-3 rounded-2 border transition-all text-center',
        active
          ? 'border-mint-deep bg-mint-soft shadow-pressed text-mint-deep'
          : 'border-card-stroke bg-card hover:border-mint-deep/30 text-fg',
      )}
    >
      <Icon className="h-5 w-5" strokeWidth={1.8} />
      <span className="text-[12px] font-semibold">{label}</span>
      <span className="text-[10px] text-fg-muted leading-tight">{hint}</span>
    </button>
  );
}
